import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { transferItemColumns } from '@utils/recordHelpers';

import { Thumbnail, Button, TextField, Tooltip, Select, Icon } from '@shopify/polaris';
import { DeleteMajorMonotone, PackageMajorMonotone } from '@shopify/polaris-icons';

import ResourceTableHeaders from '@presentational/ResourceTableHeaders';
import ResourceTableBody from '@presentational/ResourceTableBody';

import { ta } from '@utils';
import round from 'lodash/round';

const propTypes = {
  formName: PropTypes.string.isRequired, // formik name to apply updates on field
  items: PropTypes.array.isRequired,
  currencySymbol: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
}

const defaultProps = {
}

// TODO Take data from backend
const taxOptions = [
  {label: '13%', value: "13"},
  {label: '0%', value: "0"},
  {label: '1%', value: "1"},
  {label: '2%', value: "2"},
];

const TransferFormItems = (props) => {
  const { formName, errors, onChange } = props;

  console.log(props.items)

  const setupColumnsForForm = (columns) => {
    return columns.filter( col => col.formSize != 0 ).map( col => ({ ...col, size: col.formSize || col.size }) ) // use formSize property if defined, otherwise use size
  }

  const handleRemove = (itemId) => {
    props.onRemove(itemId);
  }

  // Specific function for Select component
  const handleTaxChange = (value, itemName) => {
    props.onChange(value, `${itemName}.tax_rate`);
  }

  const calculateTotals = (items) => {
    return items.reduce( (totals, item ) => {
      const itemTotalWithoutTax = Number(item.unit_cost) * Number(item.quantity);
      const itemTotalTax = (itemTotalWithoutTax * Number(item.tax_rate) )/100;

      return {
        totalQuantity: totals.totalQuantity + Number(item.quantity),
        totalWithoutTax: totals.totalWithoutTax + itemTotalWithoutTax,
        totalWithTax: totals.totalWithTax + round(itemTotalWithoutTax + itemTotalTax, 2)
      }
    }, { totalQuantity: 0, totalWithoutTax: 0, totalWithTax: 0 })
  }

  const renderItem = (item, index) => {
    const itemFormName = `${formName}.${index}`
    const unit_cost_with_tax = item.unit_cost ? round( ((Number(item.unit_cost) * Number(item.tax_rate))/100 + Number(item.unit_cost)), 2) : ""; 
    const readOnly = props.readOnly || item.shipped > 0; // SHIPPED items cannot be edited
    const searchId = item.id || item.formId;
    const isNewItem = !item.id; // Some action change whenever the item is new(creating) or not(updating). Both kind can be present on Form when updating as new items can be added

    // To Delete items are hidden to keep index consistency on list
    // Do not use ResourceItem (like the show table) because if cause a render update problem, updates are not displayed (possibly by how ResourceItem optimizations works).
    if(item["_destroy"]){
      return <div key={index}></div> }
    else{
      return(
        <div key={index} className="pv10 ph20 resource-item-copy">
          <div className={`row${item["_destroy"] ? " hidden_i" : ""}`}>
            <div className="col-md-1">
              <Thumbnail source={ item.variant.image_url } />
            </div>
            <div className="col-md-2">
              <p>{ item.variant.sku }</p>
            </div>
            <div className="col-md-2">
              <p>{ item.variant.display_name || item.variant.title }</p>
            </div>
            <div className="col-md-1">
              <TextField
                min={1}
                id={`${itemFormName}.quantity`}
                name={`${itemFormName}.quantity`}
                type="number"
                readOnly={ readOnly }
                error={ (item.quantity <= 0) && errors['products_transfers.quantity'] && errors['products_transfers.quantity'][0] }
                onChange={ onChange }
                value={item.quantity}
              />
            </div>
            <div className="col-md-2">
              <TextField
                prefix={ props.currencySymbol }
                min={1}
                id={`${itemFormName}.unit_cost`}
                name={`${itemFormName}.unit_cost`}
                type="number"
                readOnly={ readOnly }
                error={ (item.unit_cost <= 0) && errors['products_transfers.unit_cost'] && errors['products_transfers.unit_cost'][0] }
                onChange={ onChange }
                value={ item.unit_cost }
              />
            </div>
            {/* Tax currently cannot be updated after creation */}
            <div className="col-md-1">
              <Select
                options={taxOptions}
                disabled={ readOnly || !isNewItem }
                onChange={ (value) => handleTaxChange(value, itemFormName) }
                value={ item.tax_rate }
              />
            </div>
            <div className="col-md-3 flex">
              <TextField
                prefix={ props.currencySymbol }
                readOnly
                disabled
                value={ String(unit_cost_with_tax) }
              />
              <div className="ml10">
                { readOnly ?
                  <Tooltip content="Este ítem no se puede editar porque ya fue recibido.">
                    <Icon source={ PackageMajorMonotone } />
                  </Tooltip>
                :
                  <Button
                    plain
                    onClick={ () => handleRemove(searchId) }
                    icon={ DeleteMajorMonotone }>
                  </Button>
                }
              </div>
            </div>
          </div>
        </div>
      )
    }
  }

  const columnsInfo = setupColumnsForForm(transferItemColumns);

  // Calculate totals
  const totalsInformation = calculateTotals(props.items);

  return (
    <div className='transfer-form-items'>
      <ResourceTableHeaders columnsInfo={ columnsInfo } />

      {/* Manual sizing (if size change too much in the future consider setting it as dynamic) */}
      <div className="resource-items-copy-container">{ props.items.map( (item, index) => renderItem(item, index) ) }</div>

      {/* Display totals */}
      { props.items.length >= 1 &&
        <div>
          <ResourceTableBody
            items={[totalsInformation]}
            columnsInfo={[
              { size: 3, header: "", accessor: "offset" }, // up to sku
              { size: 2, header: "", accessor: () => <strong>Total:</strong> }, // title cell basically
              { size: 1, header: "", accessor: ({ totalQuantity }) => <strong>{ totalQuantity }</strong> },// same size as qty
              { size: 3, header: "", accessor: ({ totalWithoutTax }) => <strong>{ props.currencySymbol } { round(totalWithoutTax,2) }</strong> }, // take extra space from tax column
              { size: 3, header: "", accessor: ({ totalWithTax }) => <strong>{ props.currencySymbol } { round(totalWithTax,2) }</strong> },
            ]}
          />
        </div>
      }

    </div>
  )
};

TransferFormItems.propTypes = propTypes;
TransferFormItems.defaultProps = defaultProps;

export default TransferFormItems;